import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, ArrowRight, Check, Lightning, Shield,
  Wallet, Storefront, Clock, Receipt, ArrowsClockwise
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import PremiumCard from '../../components/PremiumCard';
import { useLanguage } from '../../contexts/LanguageContext';

const PaymentCard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const features = [
    {
      icon: Lightning,
      title: t('payment_instant'),
      titleEn: 'Instant Payments',
      description: t('payment_instant_desc'),
      descEn: 'Tap your phone or scan a QR code to pay.'
    },
    {
      icon: Shield,
      title: t('payment_secure'),
      titleEn: 'Bank-Level Security',
      description: t('payment_secure_desc'),
      descEn: 'Encrypted transactions, no card details stored.'
    },
    {
      icon: Receipt,
      title: t('payment_statement'),
      titleEn: 'Monthly Statement',
      description: t('payment_statement_desc'),
      descEn: 'Track your spending with detailed reports.'
    },
    {
      icon: ArrowsClockwise,
      title: t('payment_topup'),
      titleEn: 'Easy Top-Up',
      description: t('payment_topup_desc'),
      descEn: 'Recharge with card, bank transfer, or crypto.'
    }
  ];

  const tiers = [
    {
      name: 'Member',
      color: '#8BA89A',
      limit: '£500',
      features: [t('tier_basic_payments'), t('tier_2pts'), t('tier_network_access')]
    },
    {
      name: 'Reserve',
      color: '#C0C0C0',
      limit: '£2,000',
      features: [t('tier_priority_support'), t('tier_3pts'), t('tier_exclusive_offers')]
    },
    {
      name: 'Founding',
      color: '#C3A365',
      limit: '£10,000',
      features: [t('tier_concierge'), t('tier_4pts'), t('tier_vip_events')]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <CreditCard size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('payment_layer')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('payment_title_1')}<br />
            <span className="text-[#C3A365]">{t('payment_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('payment_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Card Visual */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto flex justify-center">
          <PremiumCard 
            balance={847.50} 
            tier="Reserve" 
            cardNumber="•••• •••• •••• 4821"
            size="md"
          />
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('payment_features')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('payment_features_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26]/50 border border-[#283D32]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C3A365]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C3A365]" weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{feature.title}</h3>
                  <p className="text-[#8BA89A] text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('payment_tiers')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('payment_tiers_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 border ${
                  tier.name === 'Founding'
                    ? 'bg-[#C3A365]/10 border-[#C3A365]/50'
                    : 'bg-[#1E2F26] border-[#283D32]'
                }`}
              >
                <div className="text-center mb-6">
                  <CreditCard size={40} style={{ color: tier.color }} weight="duotone" className="mx-auto mb-3" />
                  <h3 className="text-2xl font-medium" style={{ color: tier.color }}>{tier.name}</h3>
                  <p className="text-[#8BA89A] text-sm">{t('limit')}: {tier.limit}</p>
                </div>
                <ul className="space-y-3">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <Check size={16} className="text-[#C3A365]" weight="bold" />
                      <span className="text-white">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Up Methods */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('section_topup')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('section_topup_title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#1E2F26] border border-[#283D32]">
              <CreditCard size={32} className="text-[#C3A365] mb-4" weight="duotone" />
              <h3 className="text-xl text-white font-medium mb-2">{t('topup_card')}</h3>
              <p className="text-[#8BA89A] mb-4">Visa, Mastercard, American Express</p>
              <p className="text-[#C3A365] text-sm">{t('no_fees')}</p>
            </div>
            <div className="p-6 bg-[#1E2F26] border border-[#283D32]">
              <Wallet size={32} className="text-[#627EEA] mb-4" weight="duotone" />
              <h3 className="text-xl text-white font-medium mb-2">{t('topup_crypto')}</h3>
              <p className="text-[#8BA89A] mb-4">BTC · ETH · USDC · SOL · IKA</p>
              <p className="text-[#8BA89A] text-sm">{t('crypto_fee')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#0F1A14] to-[#18261E]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('payment_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('payment_cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => navigate('/activer')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('activate_my_card')}
              <ArrowRight size={20} />
            </motion.button>
            <button
              onClick={() => navigate('/offrir')}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3E5248] text-white hover:border-[#C3A365]/50 transition-colors"
            >
              {t('hero_cta')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default PaymentCard;
